import { format } from 'date-fns';
import React, { type MouseEvent, type ReactNode, useMemo } from 'react';

import { DeleteIcon, EditIcon } from '../../../icons';
import type { ResourceMenuItem } from '../../organisms/ResourceMenuActions';

export type QuoteStatus = 'pending' | 'accepted' | 'refused';

export function normalizeQuoteStatus(status?: string | null): QuoteStatus {
  if (status === 'accepted' || status === 'approved') return 'accepted';
  if (status === 'refused' || status === 'rejected') return 'refused';
  return 'pending';
}

export type QuoteDocument = {
  id?: string | null;
  name?: string | null;
  url?: string | null;
  file_url?: string | null;
  type?: string | null;
  document_types?: { id?: string; name?: string } | null;
};

export type QuoteItem = {
  id?: string;
  title?: string | null;
  comment?: string | null;
  amount?: number | null;
  status?: QuoteStatus | string | null;
  providerName?: string | null;
  provider_name?: string | null;
  providerId?: string | null;
  provider_id?: string | null;
  providers?: { id?: string; name: string } | null;
  dateApproved?: string | Date | null;
  date_approved?: string | Date | null;
  approvedDocumentUrl?: string | null;
  approved_document_url?: string | null;
  documents?: QuoteDocument[] | null;
};

export type QuoteCardAction = ResourceMenuItem;

export type QuoteCardProps = {
  quote: QuoteItem;
  maxCommentLines?: number;
  providerHref?: string;
  onProviderClick?: (event?: MouseEvent<HTMLAnchorElement | HTMLElement>) => void;
  onEdit?: (quote: QuoteItem) => void;
  onDelete?: (quote: QuoteItem) => void;
  onAccept?: (quote: QuoteItem) => void;
  onRefuse?: (quote: QuoteItem) => void;
  onResetToPending?: (quote: QuoteItem) => void;
  onAttachSignedDocument?: (quote: QuoteItem) => void;
  action?: ReactNode;
  actions?: (QuoteCardAction | null | undefined | false)[];
  actionsButtonTooltip?: string;
  actionsButtonAriaLabel?: string;
  footerActions?: ReactNode;
  'data-testid'?: string;
};

export function useQuoteCard(props: QuoteCardProps) {
  const normalizedStatus = normalizeQuoteStatus(props.quote.status);

  const formattedAmount = useMemo(() => {
    if (props.quote.amount == null || typeof props.quote.amount !== 'number' || Number.isNaN(props.quote.amount)) {
      return null;
    }
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(props.quote.amount);
  }, [props.quote.amount]);

  const dateApprovedRaw = props.quote.dateApproved ?? props.quote.date_approved;
  const formattedDateApproved = useMemo(() => {
    if (!dateApprovedRaw) return null;
    try {
      const dateObj = typeof dateApprovedRaw === 'string' ? new Date(dateApprovedRaw) : dateApprovedRaw;
      if (Number.isNaN(dateObj.getTime())) return null;
      return format(dateObj, 'dd/MM/yyyy');
    } catch {
      return null;
    }
  }, [dateApprovedRaw]);

  const providerName =
    props.quote.providers?.name?.trim() ||
    props.quote.providerName?.trim() ||
    props.quote.provider_name?.trim() ||
    'Prestataire non renseigné';

  const providerId =
    props.quote.providerId ||
    props.quote.provider_id ||
    props.quote.providers?.id ||
    null;

  const hasComment = useMemo(() => {
    if (!props.quote.comment) return false;
    return props.quote.comment.trim().length > 0;
  }, [props.quote.comment]);

  const rawApprovedUrl = props.quote.approvedDocumentUrl ?? props.quote.approved_document_url;
  const documents = props.quote.documents ?? [];

  const bpaDoc = useMemo(() => {
    return (
      documents.find(
        (doc) =>
          (rawApprovedUrl && (doc.file_url === rawApprovedUrl || doc.url === rawApprovedUrl)) ||
          doc.document_types?.name?.toLowerCase() === 'bon pour accord' ||
          doc.type?.toLowerCase() === 'bon pour accord' ||
          doc.name?.toLowerCase().includes('accord') ||
          doc.name?.toLowerCase().includes('signé'),
      ) ?? null
    );
  }, [documents, rawApprovedUrl]);

  const bpaUrl = rawApprovedUrl || bpaDoc?.file_url || bpaDoc?.url || null;
  const bpaName = bpaDoc?.name || 'Bon pour accord';

  const quoteDocuments = useMemo(() => {
    return documents.filter((doc) => {
      const url = doc.file_url ?? doc.url;
      if (bpaUrl && url === bpaUrl) return false;
      if (bpaDoc?.id && doc.id === bpaDoc.id) return false;
      return true;
    });
  }, [documents, bpaUrl, bpaDoc?.id]);

  function handleEdit() {
    props.onEdit?.(props.quote);
  }

  function handleDelete() {
    props.onDelete?.(props.quote);
  }

  const actions = useMemo(() => {
    const customActions = (props.actions ?? []).filter((action): action is QuoteCardAction => Boolean(action));
    const hasCustomEdit = customActions.some((a) => 'id' in a && a.id === 'edit');
    const hasCustomDelete = customActions.some((a) => 'id' in a && a.id === 'delete');

    const result: QuoteCardAction[] = [];

    if (props.onEdit && !hasCustomEdit) {
      result.push({
        id: 'edit',
        label: 'Modifier',
        icon: React.createElement(EditIcon, { fontSize: 'small' }),
        onClick: handleEdit,
      });
    }

    result.push(...customActions);

    if (props.onDelete && !hasCustomDelete) {
      result.push({
        id: 'delete',
        label: 'Supprimer',
        icon: React.createElement(DeleteIcon, { fontSize: 'small' }),
        onClick: handleDelete,
      });
    }

    return result;
  }, [props.actions, props.onEdit, props.onDelete, props.quote]);

  function handleAccept() {
    props.onAccept?.(props.quote);
  }

  function handleRefuse() {
    props.onRefuse?.(props.quote);
  }

  function handleResetToPending() {
    props.onResetToPending?.(props.quote);
  }

  function handleAttachBpa() {
    props.onAttachSignedDocument?.(props.quote);
  }

  const hasActions = actions.length > 0;
  const showAttachBpaButton = Boolean(props.onAttachSignedDocument && !bpaUrl);

  const hasFooterActions = Boolean(
    props.footerActions ||
      (normalizedStatus === 'pending' && (props.onRefuse || props.onAccept)) ||
      (normalizedStatus === 'accepted' && (props.onResetToPending || showAttachBpaButton)) ||
      (normalizedStatus === 'refused' && props.onResetToPending),
  );

  const actionsButtonTooltip = props.actionsButtonTooltip ?? 'Options du devis';
  const actionsButtonAriaLabel = props.actionsButtonAriaLabel ?? actionsButtonTooltip;

  return {
    normalizedStatus,
    formattedAmount,
    formattedDateApproved,
    providerName,
    providerId,
    bpaDoc,
    bpaUrl,
    bpaName,
    quoteDocuments,
    hasDocuments: quoteDocuments.length > 0 || Boolean(bpaUrl) || Boolean(bpaDoc),
    actions,
    hasActions,
    showAttachBpaButton,
    hasFooterActions,
    actionsButtonTooltip,
    actionsButtonAriaLabel,
    hasComment,
    handleEdit,
    handleDelete,
    handleAccept,
    handleRefuse,
    handleResetToPending,
    handleAttachBpa,
  };
}
